import { hasEnoughBylineMenuLinks } from './selectBylineMenuLinks.ts';
import type { EnhancedSmartLink } from './smartLinkTypes.ts';

export type BylineVisibilityInput = {
	/** Embedded Confluence renders a different menu and keeps the earlier show/hide rule. */
	isEmbeddedMenu: boolean;
	outgoingLinks: readonly EnhancedSmartLink[];
	incomingLinks: readonly EnhancedSmartLink[];
	relatedContentLinks: readonly EnhancedSmartLink[];
	hasDevDocsConnection: boolean;
	outgoingLinksLoading: boolean;
	relatedContentLoading: boolean;
	/** No pending source can change the icon stack; see `isBylineConnectionIconStackFinal`. */
	isIconStackFinal: boolean;
	/**
	 * The server's samples saw an outbound link, an incoming link or a Dev Docs connection. Holds
	 * only the embedded byline open; the tile count the placeholder painted is a separate matter.
	 */
	hasSsrConnectionSignal: boolean;
	/**
	 * The server painted tiles for this first page load. The byline then stays even if fewer links
	 * than the row minimum resolve on the client, so the header never loses width after paint; the
	 * spare tiles stay grey. CCINT-9543
	 */
	hasServerTiles: boolean;
};

export type BylineVisibility = {
	/** The page has something for the byline to show once loading settles. */
	hasConnection: boolean;
	/** Keep the placeholder up; a source that could still change the outcome is pending. */
	isResolving: boolean;
	/** Render the loaded trigger once `isResolving` clears. */
	shouldRenderByline: boolean;
};

/**
 * Decides whether the byline shows, and whether it must keep waiting, from the three link sources
 * and their loading state.
 *
 * Default menu (CCINT-9351): the byline shows once Related content, Appears in and Links to together
 * reach the row minimum, or the page has a Dev Docs connection, which keeps its own entry point.
 * Embedded menu: any outbound link, Dev Docs connection or incoming link shows it, as before.
 *
 * Waiting (CCINT-9308): the trigger paints once, with every icon in place, so a pending source is
 * awaited unless the sources ahead of it already fill the stack (CCINT-9503). Related content leads
 * the stack, so both menus wait for it; that also keeps a page that never qualifies from flashing in
 * and out.
 *
 * A server-side connection signal holds only the embedded byline open on its own: the outbound
 * preload cannot know whether a page reaches the row minimum, so a short default page collapses
 * instead.
 */
export const getBylineVisibility = ({
	isEmbeddedMenu,
	outgoingLinks,
	incomingLinks,
	relatedContentLinks,
	hasDevDocsConnection,
	outgoingLinksLoading,
	relatedContentLoading,
	isIconStackFinal,
	hasSsrConnectionSignal,
	hasServerTiles,
}: BylineVisibilityInput): BylineVisibility => {
	const hasConnection = isEmbeddedMenu
		? outgoingLinks.length > 0 || hasDevDocsConnection || incomingLinks.length > 0
		: hasDevDocsConnection ||
			hasEnoughBylineMenuLinks({ relatedContentLinks, incomingLinks, outgoingLinks });
	const isResolving =
		outgoingLinksLoading ||
		!isIconStackFinal ||
		(!isEmbeddedMenu && !hasConnection && relatedContentLoading);
	const shouldRenderByline =
		hasConnection || hasServerTiles || (isEmbeddedMenu && hasSsrConnectionSignal);
	return { hasConnection, isResolving, shouldRenderByline };
};
